'use client'

import { useState } from 'react'
import classNames from 'classnames'
import styles from './ContactForm.module.scss'
import Button from '@/components/Button/Button'
import useI18n, { I18nFunction } from '@/hooks/use-i18n'
import ContactFormResults from './ContactFormResults/ContactFormResults'
import { handleTimeOfVisit, handleDateOfVisit, handlePhoneNumber, validateField } from '@/utils/contactForm'
import FormField, { FormFieldChangeEvent, FormFieldProps } from '@/components/FormField/FormField'
import useCurrentPage from '@/hooks/use-current-page'
import { useFormSubmissionState } from '@/hooks/useFormSubmissionState'

const FormResults = ({
  validationErrors,
  formSubmit,
  loading,
  formSubmitSuccess,
  i18n,
}: {
  validationErrors: { field: string; errorMessage: string }[]
  formSubmit: boolean
  loading: boolean
  formSubmitSuccess: boolean
  i18n: I18nFunction
}) => {
  if (validationErrors.length > 0) {
    return (
      <ContactFormResults
        status="error"
        errorMessage={<p>{i18n('pleaseFillTheRequiredFields')}</p>}
      />
    )
  }

  if (formSubmit && !loading) {
    if (formSubmitSuccess) {
      return (
        <ContactFormResults
          data-gtm-visibility="success-message"
          status="success"
          successMessage={<p>{i18n('thankYouForContactingUs')}</p>}
        />
      )
    } else {
      return (
        <ContactFormResults
          status="error"
          errorMessage={<p>{i18n('somethingWentWrong')}</p>}
        />
      )
    }
  }

  return null
}

export type ContactFormField =
  | 'reasonForContactingUs'
  | 'firstName'
  | 'lastName'
  | 'emailAddress'
  | 'phoneNumber'
  | 'locationVisited'
  | 'dateOfVisit'
  | 'timeOfVisit'
  | 'message'

const ContactForm = ({ className }: ContactFormProps) => {
  const { i18n } = useI18n()
  const { currentLanguage } = useCurrentPage()

  const langConfig = {
    language: currentLanguage,
  }

  const locationsVisited = [
    i18n('vancouverBritishColumbia'),
    i18n('californiaArcadia'),
    i18n('californiaCostaMesa'),
    i18n('californiaDowntownDisney'),
    i18n('californiaGlendale'),
    i18n('californiaLosAngeles'),
    i18n('californiaSanDiego'),
    i18n('californiaSantaClara'),
    i18n('californiaSantaMonica'),
    i18n('californiaTorrance'),
    i18n('nevadaLasVegas'),
    i18n('newYorkNyc'),
    i18n('oregonPortland'),
    i18n('oregonTigard'),
    i18n('washingtonBellevue'),
    i18n('washingtonSeattlePacificPlace'),
    i18n('washingtonSeattleUniversityVillage'),
    i18n('washingtonTukwila'),
  ]

  const reasonForContactingUs = [
    i18n('restaurantExperience'),
    i18n('mediaInquiries'),
    i18n('DonationRequests'),
    i18n('businessRequests'),
    i18n('generalQuestions'),
    i18n('other'),
  ]

  const handleChange = (fieldName: string) => (event: FormFieldChangeEvent) => {
    setValidationErrors(prevState => prevState.filter(error => error.field !== fieldName))

    const { value } = event?.target || { value: '' }
    setFormData(prevState => ({
      ...prevState,
      [fieldName]: value,
    }))
  }

  const { loading, formSubmit, formSubmitSuccess, startSubmission, finishSubmission } = useFormSubmissionState()
  const [validationErrors, setValidationErrors] = useState<{ field: string; errorMessage: string }[]>([])

  const defaultErrorMessage = i18n('defaultFieldError') ?? 'Error'
  const formFields: Record<ContactFormField, FormFieldProps> = {
    reasonForContactingUs: {
      name: 'reasonForContactingUs',
      value: '',
      required: true,
      errorMessage: i18n('requiredField'),
      className: styles.field,
      type: 'select',
      placeholder: i18n('reasonForContactingUs'),
      onChange: handleChange('reasonForContactingUs'),
      ariaLabel: i18n('reasonForContactingUs'),
      items: reasonForContactingUs,
    },
    firstName: {
      name: 'firstName',
      value: '',
      required: true,
      errorMessage: i18n('requiredField'),
      className: styles.field,
      type: 'text',
      placeholder: i18n('firstName'),
      onChange: handleChange('firstName'),
    },
    lastName: {
      name: 'lastName',
      value: '',
      required: true,
      errorMessage: i18n('requiredField'),
      className: styles.field,
      type: 'text',
      placeholder: i18n('lastName'),
      onChange: handleChange('lastName'),
    },
    emailAddress: {
      name: 'emailAddress',
      value: '',
      required: true,
      errorMessage: i18n('invalidEmailAddress'),
      className: styles.field,
      type: 'text',
      placeholder: i18n('emailAddress'),
      onChange: handleChange('emailAddress'),
      autoComplete: 'email',
    },
    phoneNumber: {
      name: 'phoneNumber',
      value: '',
      required: true,
      errorMessage: i18n('invalidPhoneNumber'),
      className: styles.field,
      type: 'text',
      placeholder: i18n('phoneNumber'),
      onChange: (e: FormFieldChangeEvent) => handlePhoneNumber('phoneNumber', e, handleChange),
    },
    locationVisited: {
      name: 'locationVisited',
      value: '',
      required: false,
      errorMessage: undefined,
      className: styles.field,
      type: 'select',
      onChange: () => handleChange('locationVisited'),
      items: locationsVisited,
      ariaLabel: i18n('locationVisited'),
    },
    dateOfVisit: {
      name: 'dateOfVisit',
      value: '',
      required: false,
      errorMessage: i18n('invalidDateOfVisit'),
      className: styles.field,
      type: 'date',
      placeholder: i18n('dateOfVisit'),
      onChange: (e: FormFieldChangeEvent) => handleDateOfVisit('dateOfVisit', e, handleChange),
      min: '2020-01-01',
      max: new Date().toISOString().split('T')[0],
    },
    timeOfVisit: {
      name: 'timeOfVisit',
      value: '',
      required: false,
      errorMessage: i18n('invalidTimeOfVisit'),
      className: styles.field,
      type: 'text',
      placeholder: i18n('timeOfVisit'),
      onChange: (e: FormFieldChangeEvent) => handleTimeOfVisit('timeOfVisit', e, handleChange),
    },
    message: {
      name: 'message',
      value: '',
      required: true,
      errorMessage: i18n('requiredField'),
      className: styles.field,
      type: 'textarea',
      placeholder: i18n('message'),
      onChange: handleChange('message'),
    },
  } as const
  const intialState = Object.entries(formFields).reduce(
    (acc, [fieldName, fieldMetadata]) => ({ ...acc, [fieldName]: fieldMetadata.value }),
    {} as { -readonly [key in keyof typeof formFields]: string },
  )

  const [formData, setFormData] = useState<{ [key: string]: string }>(intialState)

  const getFormFieldProps = (fieldName: keyof typeof formFields): FormFieldProps => {
    return {
      ...formFields[fieldName],
      value: formData[fieldName],
      errorMessage: validationErrors.find(error => error.field === fieldName)?.errorMessage,
    }
  }

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const validationErrors: ValidationError[] = Object.values(formFields)
      .map(field => {
        if (field.required && !formData[field.name]) {
          return { field: field.name, errorMessage: field.errorMessage || defaultErrorMessage }
        }

        const specialValidationFields = ['emailAddress', 'phoneNumber', 'dateOfVisit', 'timeOfVisit']

        if (specialValidationFields.includes(field.name)) {
          const isValid = validateField(field.name, formData[field.name])
          if (!isValid && formData[field.name]) {
            return { field: field.name, errorMessage: field.errorMessage || defaultErrorMessage }
          }
        }
        return null
      })
      .filter(Boolean) as ValidationError[]

    setValidationErrors(validationErrors)

    if (validationErrors.length > 0) {
      finishSubmission(false)
      return
    }

    startSubmission()

    try {
      /**
       * Fetch API to send the form data to the server
       */

      const formDataAndConfig = {
        ...formData,
        ...langConfig,
      }

      const response = await fetch('/api/contact-form', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formDataAndConfig),
      })

      if (!response.ok) {
        // Handle 400 (validation), 500 (server error), 503 (service unavailable)
        const errorData = await response.json().catch(() => ({ success: false }))
        console.error('Contact form submission failed:', errorData?.message || response.statusText)
        finishSubmission(false)
        return
      }

      const data = await response.json()
      finishSubmission(data?.success === true)
      if (data?.success) {
        setFormData(intialState)
      }
    } catch (error) {
      finishSubmission(false)
      console.error({ error })
    }
  }

  return (
    <div className={classNames(className, styles.ContactForm)}>
      <form
        onSubmit={e => onSubmit(e)}
        noValidate
        autoComplete="on"
        name="ContactForm"
      >
        <div className={styles.formFields}>
          <div className={styles.fieldsContainer}>
            <FormField {...getFormFieldProps('reasonForContactingUs')} />
          </div>
          <div className={styles.fieldsContainer}>
            <FormField {...getFormFieldProps('firstName')} />
            <FormField {...getFormFieldProps('lastName')} />
          </div>
          <div className={styles.fieldsContainer}>
            <FormField {...getFormFieldProps('emailAddress')} />
            <FormField {...getFormFieldProps('phoneNumber')} />
          </div>
          <div className={styles.fieldsContainer}>
            <FormField
              {...getFormFieldProps('locationVisited')}
              placeholder={`${i18n('locationVisited')}${
                formData.reasonForContactingUs === i18n('restaurantExperience') ? ' *' : ''
              }`}
              required={formData.reasonForContactingUs === i18n('restaurantExperience')}
            />
          </div>
          <div className={styles.fieldsContainer}>
            <FormField {...getFormFieldProps('dateOfVisit')} />
            <FormField {...getFormFieldProps('timeOfVisit')} />
          </div>
          <div className={styles.fieldsContainer}>
            <FormField {...getFormFieldProps('message')} />
          </div>
        </div>
        <div className={styles.submitContainer}>
          <Button
            className={styles.submitButton}
            type="submit"
            secondary
            icon="caretRight"
            label={loading ? i18n('pleaseWait') : i18n('submit')}
            disabled={loading}
          />
        </div>
      </form>
      <div
        className={styles.FormResults}
        id="contact_form_results"
      >
        <FormResults
          validationErrors={validationErrors}
          formSubmit={formSubmit}
          loading={loading}
          formSubmitSuccess={formSubmitSuccess}
          i18n={i18n}
        />
      </div>
    </div>
  )
}

export default ContactForm
